import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../lib/format';
import { formatMoney, isPositive } from '../../../lib/money';
import { Barcode } from '../Barcode';
import { DocFrame, Row } from '../DocFrame';
import { PrintLines } from '../PrintLines';
import type { PrintDocProps } from '../print-types';
import { registerPrintDoc } from '../print-registry';
import type { InvoicePrint } from '../types';

export function InvoiceDoc({ data, size, onReady }: PrintDocProps<InvoicePrint>): ReactNode {
  const { t } = useTranslation();
  const barcode = size === '80mm' && data.counterOrderNo !== null ? data.counterOrderNo : null;
  return (
    <DocFrame title={t('print.title.invoice')} meta={data.meta} hasBarcode={barcode !== null} onReady={onReady}>
      {data.voided === null ? null : (
        <div className="print-stamp">
          <div className="print-stamp-word">{t('print.voided')}</div>
          <div className="print-small">
            {t('print.voidedBy', { creditNoteNo: data.voided.creditNoteNo, date: formatDateTime(data.voided.voidedAt) })}
          </div>
        </div>
      )}
      <Row label={t('print.number')} value={data.invoiceNo} />
      <Row label={t('print.dateTime')} value={formatDateTime(data.issuedAt)} />
      {data.jobNo === null ? null : <Row label={t('print.jobNo')} value={data.jobNo} />}
      {data.plateNo === null ? null : <Row label={t('print.plateNo')} value={data.plateNo} />}
      {data.bookingNo === null ? null : <Row label={t('print.bookingNo')} value={data.bookingNo} />}
      <section className="print-block">
        <div className="print-label">{t('print.customer')}</div>
        {data.customer === null ? (
          <div>{t('print.walkIn')}</div>
        ) : (
          <>
            <div>{data.customer.name}</div>
            {data.customer.phone === null ? null : <div className="print-small">{data.customer.phone}</div>}
            {data.customer.addressLines.map((line) => (
              <div key={line} className="print-small">
                {line}
              </div>
            ))}
          </>
        )}
      </section>
      <PrintLines lines={data.lines} size={size} />
      <section className="print-block print-totals">
        <Row label={t('print.subtotal')} value={formatMoney(data.subtotal)} />
        {isPositive(data.discountTotal) ? (
          <Row label={t('print.discount')} value={`-${formatMoney(data.discountTotal)}`} />
        ) : null}
        {data.taxes.map((tax) => (
          <Row key={tax.code} label={t('print.tax', { code: tax.code, rate: tax.rate })} value={formatMoney(tax.amount)} />
        ))}
        <Row label={t('print.total')} value={<strong>{formatMoney(data.grandTotal)}</strong>} />
        {data.voided === null && isPositive(data.creditedTotal) ? (
          <Row label={t('print.credited')} value={formatMoney(`-${data.creditedTotal}`)} />
        ) : null}
        {data.voided === null ? (
          <>
            <Row label={t('print.paid')} value={formatMoney(data.paidTotal)} />
            <Row label={t('print.balanceDue')} value={<strong>{formatMoney(data.balanceDue)}</strong>} />
          </>
        ) : null}
      </section>
      {data.payments.length === 0 ? null : (
        <section className="print-block">
          <div className="print-label">{t('print.payments')}</div>
          {data.payments.map((p) => (
            <Row
              key={p.receiptNo}
              label={`${p.receiptNo} · ${t(`print.method.${p.method}`)} · ${formatDateTime(p.receivedAt)}`}
              value={formatMoney(p.amount)}
            />
          ))}
        </section>
      )}
      {data.issuedBy === null ? null : <div className="print-small">{t('print.issuedBy', { name: data.issuedBy })}</div>}
      {barcode === null ? null : (
        <div className="print-barcode-wrap">
          <Barcode value={barcode} onReady={onReady} />
        </div>
      )}
    </DocFrame>
  );
}

registerPrintDoc<InvoicePrint>('invoice', InvoiceDoc);
